import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    InputAdornment,
    Paper,
    Stack,
    TextField,
    Typography,
    useTheme,
    useMediaQuery
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { AnimatedPasswordCheck } from "../components/PasswordCheck.tsx";
import { isStrongPassword } from "../../../src/shared";
import { fetchWithError } from "../utils/fetchWithError.ts";
import { useSnackbar } from "notistack";
import tintoaLogoWhite from "../img/logo_tintoa_white.png";
import tintoaLogoBlack from "../img/logo_tintoa_black.png";
import { TermsAndConditions } from "../components/TermsAndConditions.tsx";
import { AuthPageShell } from "../components/auth/AuthPageShell.tsx";
import type {
    IInvitationClaimInfo,
    IInvitationClaimResult
} from "@ids/shared";

/**
 * Account setup form reached from an invitation mail link.
 * Only password + terms are required; name is prefilled, email is fixed.
 * On submit the backend creates the account, grants access and starts a
 * session – the browser goes straight into the invited application.
 */
export const CompleteInviteScreen = () => {
    const token = new URLSearchParams(window.location.search).get("token") ?? "";
    const [info, setInfo] = useState<IInvitationClaimInfo | null>(null);
    const [formData, setFormData] = useState({
        firstname: "",
        lastname: "",
        password: "",
        confirmPassword: ""
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordHint, setShowPasswordHint] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [showAcceptTermsError, setShowAcceptTermsError] = useState(false);
    const navigate = useNavigate();
    const theme = useTheme();
    const isDarkTheme = theme.palette.mode === "dark";
    const { enqueueSnackbar } = useSnackbar();
    const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"));

    useEffect(() => {
        const loadInfo = async () => {
            try {
                const result: IInvitationClaimInfo = await fetchWithError(
                    `/auth/invite/info?token=${encodeURIComponent(token)}`,
                    { credentials: "include" }
                );
                if (!result.valid) {
                    navigate("/app/invite-expired", { replace: true });
                    return;
                }
                if (result.sessionMismatch) {
                    navigate(`/app/invite-mismatch?token=${encodeURIComponent(token)}`, {
                        replace: true
                    });
                    return;
                }
                if (!result.requiresAccountSetup) {
                    // Account already exists: the claim endpoint logs in directly.
                    window.location.href = `/auth/invite?token=${encodeURIComponent(token)}`;
                    return;
                }
                setInfo(result);
                setFormData((prev) => ({
                    ...prev,
                    firstname: result.firstname ?? "",
                    lastname: result.lastname ?? ""
                }));
            } catch {
                navigate("/app/invite-expired", { replace: true });
            }
        };
        loadInfo();
    }, [token, navigate]);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (formData.password !== formData.confirmPassword) {
            enqueueSnackbar("Die Passwörter stimmen nicht überein.", {
                variant: "error"
            });
            return;
        }
        if (!acceptTerms) {
            setShowAcceptTermsError(true);
            enqueueSnackbar(
                "Bitte akzeptiere Nutzungsbedingungen und Datenschutzerklärung.",
                { variant: "error" }
            );
            return;
        }
        setShowAcceptTermsError(false);
        setIsLoading(true);

        try {
            const result: IInvitationClaimResult = await fetchWithError(
                "/auth/invite/complete",
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        token,
                        password: formData.password,
                        acceptTerms: true,
                        firstname: formData.firstname,
                        lastname: formData.lastname
                    }),
                    credentials: "include"
                }
            );
            window.location.href = result.redirectUrl;
        } catch (error) {
            if (error instanceof Error) {
                enqueueSnackbar(error.message, { variant: "error" });
            } else {
                enqueueSnackbar(
                    "Konto konnte nicht eingerichtet werden. Bitte überprüfe deine Eingaben.",
                    { variant: "error" }
                );
            }
            setIsLoading(false);
        }
    };

    return (
        <AuthPageShell
            isDarkTheme={isDarkTheme}
            desktopSidePanel={
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        color: isDarkTheme ? "white" : "text.primary",
                        textAlign: "center",
                        px: 8,
                        py: 4,
                        transition: "all 0.4s ease-in-out"
                    }}
                >
                    <img
                        src={isDarkTheme ? tintoaLogoWhite : tintoaLogoBlack}
                        alt="tintoa-logo"
                        style={{ height: 48, marginBottom: 32 }}
                    />
                    <Typography
                        variant="h2"
                        component="h1"
                        gutterBottom
                        sx={{ fontWeight: 700, mb: 2 }}
                    >
                        Fast geschafft
                    </Typography>
                    <Typography
                        variant="h5"
                        sx={{ opacity: 0.9, maxWidth: 400, lineHeight: 1.6 }}
                    >
                        {info?.clientAppName
                            ? `Du wurdest zu ${info.clientAppName} eingeladen. Vergib nur noch ein Passwort – dann geht es direkt los.`
                            : "Vergib nur noch ein Passwort – dann geht es direkt los."}
                    </Typography>
                </Box>
            }
        >
            <Box
                sx={{
                    width: "100%",
                    maxWidth: { xs: 600, lg: "100%" },
                    mx: "auto",
                    display: "flex",
                    flexDirection: "column",
                    flex: { lg: 1 },
                    minHeight: { lg: 0 },
                    height: { lg: "100%" },
                    transition: "all 0.3s ease-in-out"
                }}
            >
                <Paper
                    elevation={0}
                    sx={{
                        height: isLargeScreen ? "100%" : "auto",
                        display: "flex",
                        flexDirection: "column",
                        p: { xs: 3, sm: 4, lg: 5 },
                        border: 1,
                        borderColor: "divider",
                        borderRadius: isLargeScreen ? 0 : 2,
                        bgcolor: theme.palette.authCard!,
                        backdropFilter: "blur(25px)",
                        boxShadow: "none",
                        transition: "all 0.3s ease-in-out"
                    }}
                >
                    {!info ? (
                        <Box
                            sx={{
                                flex: 1,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                minHeight: 320
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Stack
                            spacing={4}
                            sx={{
                                flex: isLargeScreen ? 1 : "none",
                                justifyContent: isLargeScreen
                                    ? "center"
                                    : "flex-start"
                            }}
                        >
                            <Box sx={{ textAlign: "center" }}>
                                {!isLargeScreen && (
                                    <img
                                        src={
                                            isDarkTheme
                                                ? tintoaLogoWhite
                                                : tintoaLogoBlack
                                        }
                                        alt="tintoa-logo"
                                        style={{ height: 32, marginBottom: 24 }}
                                    />
                                )}
                                <Typography
                                    variant="h4"
                                    component="h1"
                                    gutterBottom
                                    sx={{ color: "text.primary" }}
                                >
                                    Konto einrichten
                                </Typography>
                                <Typography
                                    sx={{
                                        color: isDarkTheme
                                            ? "text.secondary"
                                            : "text.primary",
                                        opacity: isDarkTheme ? 1 : 0.7
                                    }}
                                >
                                    {info.clientAppName
                                        ? `Für deinen Zugang zu ${info.clientAppName} fehlt nur noch ein Passwort.`
                                        : "Es fehlt nur noch ein Passwort."}
                                </Typography>
                            </Box>

                            <form onSubmit={handleSubmit}>
                                <Stack spacing={3}>
                                    <TextField
                                        label="E-Mail"
                                        value={info.email ?? ""}
                                        fullWidth
                                        disabled
                                        size={isLargeScreen ? "medium" : "small"}
                                        helperText="Die Einladung gilt für diese E-Mail-Adresse."
                                    />
                                    <Stack
                                        direction={{ xs: "column", sm: "row" }}
                                        spacing={2}
                                    >
                                        <TextField
                                            label="Vorname"
                                            name="firstname"
                                            value={formData.firstname}
                                            onChange={handleChange}
                                            required
                                            fullWidth
                                            size={
                                                isLargeScreen
                                                    ? "medium"
                                                    : "small"
                                            }
                                        />
                                        <TextField
                                            label="Nachname"
                                            name="lastname"
                                            value={formData.lastname}
                                            onChange={handleChange}
                                            required
                                            fullWidth
                                            size={
                                                isLargeScreen
                                                    ? "medium"
                                                    : "small"
                                            }
                                        />
                                    </Stack>
                                    <TextField
                                        label="Passwort"
                                        name="password"
                                        type={showPassword ? "text" : "password"}
                                        value={formData.password}
                                        onChange={(e) => {
                                            handleChange(e);
                                            if (!showPasswordHint) {
                                                setShowPasswordHint(true);
                                            }
                                        }}
                                        required
                                        fullWidth
                                        autoComplete="new-password"
                                        size={isLargeScreen ? "medium" : "small"}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() =>
                                                            setShowPassword(
                                                                !showPassword
                                                            )
                                                        }
                                                        edge="end"
                                                        size={
                                                            isLargeScreen
                                                                ? "medium"
                                                                : "small"
                                                        }
                                                    >
                                                        {showPassword ? (
                                                            <VisibilityOff />
                                                        ) : (
                                                            <Visibility />
                                                        )}
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                    <AnimatedPasswordCheck
                                        show={
                                            showPasswordHint &&
                                            !isStrongPassword(formData.password)
                                                .isStrongPassword
                                        }
                                        password={formData.password}
                                    />
                                    <TextField
                                        label="Passwort bestätigen"
                                        name="confirmPassword"
                                        type={showPassword ? "text" : "password"}
                                        value={formData.confirmPassword}
                                        onChange={handleChange}
                                        required
                                        fullWidth
                                        autoComplete="new-password"
                                        size={isLargeScreen ? "medium" : "small"}
                                        error={
                                            formData.password !==
                                                formData.confirmPassword &&
                                            formData.confirmPassword !== ""
                                        }
                                        helperText={
                                            formData.password !==
                                                formData.confirmPassword &&
                                            formData.confirmPassword !== ""
                                                ? "Die Passwörter stimmen nicht überein"
                                                : ""
                                        }
                                    />
                                    <TermsAndConditions
                                        method="signup"
                                        acceptTerms={acceptTerms}
                                        onAcceptTermsChange={(checked) => {
                                            setAcceptTerms(checked);
                                            if (checked) {
                                                setShowAcceptTermsError(false);
                                            }
                                        }}
                                        showAcceptTermsError={
                                            showAcceptTermsError
                                        }
                                    />
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        size={isLargeScreen ? "large" : "medium"}
                                        disabled={isLoading || !acceptTerms}
                                        fullWidth
                                        sx={{
                                            py: isLargeScreen ? 1.5 : 1,
                                            fontSize: isLargeScreen
                                                ? "1.1rem"
                                                : "1rem"
                                        }}
                                    >
                                        {isLoading
                                            ? "Wird eingerichtet..."
                                            : "Konto einrichten & loslegen"}
                                    </Button>
                                </Stack>
                            </form>
                        </Stack>
                    )}
                </Paper>
            </Box>
        </AuthPageShell>
    );
};
